/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState } from 'react';
import { useLocation, Link, Location } from 'react-router';
import { BreadcrumbNavigation, BreadcrumbItem } from '@entur/menu';
import { useIntl } from 'react-intl';
import { v4 as uuid } from 'uuid';
import styles from './styles.module.scss';
import { useOptimisticSearchParams } from 'nuqs/adapters/react-router/v7';

const Breadcrumbs = () => {
  const [locationPath, setLocationPath] = useState<Array<Location>>([]);

  const searchParams = useOptimisticSearchParams();
  const location = useLocation();

  const getCurrentLocation = () => {
    const searchString = searchParams ? `?${searchParams.toString()}` : '';
    const currentLocation = { ...location, search: searchString };
    return currentLocation;
  };

  const intl = useIntl();

  const checkCorrectPath = (currentLocation: Location) => {
    // copy state array
    const locationArray = locationPath.map((loc) => loc);

    if (locationArray.length > 0) {
      // if there are more than 8 paths, remove the oldest
      const maximumCrumbs = 8;
      if (locationPath.length >= maximumCrumbs) {
        locationArray.splice(0, locationArray.length - maximumCrumbs);
      }

      locationArray.forEach((exisitingLocation, index: number) => {
        const oldPath = exisitingLocation.pathname;
        const oldPathArr = oldPath.split('/').filter((string) => string.length);
        const newHistoryPath = currentLocation.pathname;
        const newHistoryArr = newHistoryPath.split('/').filter((string) => string.length);
        if (oldPath === newHistoryPath) {
          locationArray.splice(index, locationArray.length - index);
        } else if (oldPathArr.length === newHistoryArr.length && oldPathArr[0] === newHistoryArr[0]) {
          locationArray.splice(index, locationArray.length - index);
        }
      });

      locationArray.push(currentLocation);
    } else {
      locationArray.push(currentLocation);
    }

    return locationArray;
  };

  const shouldRestoreLocationPath = () => {
    const pathNames = ['/', '/callback', '/oops', '/login'];
    return pathNames.includes(location.pathname);
  };

  // Update breadcrumbs when location (pathname) or searchParams change
  useEffect(() => {
    // Skip update if there is a mismatch between location pathname and window location pathname
    if (location.pathname !== window.location.pathname) return;

    const currentLocation = getCurrentLocation();

    if (shouldRestoreLocationPath()) {
      if (locationPath.length !== 0) {
        setLocationPath([]);
      }
    } else if (location?.state?.restoreCrumbs) {
      setLocationPath([currentLocation]);
    } else {
      const correctPath = checkCorrectPath(currentLocation);
      setLocationPath(correctPath);
    }
  }, [location, searchParams]); //eslint-disable-line

  const formatCrumbs = () =>
    locationPath.map((elem) => {
      const pages = elem.pathname.split('/');
      const page = pages[pages.length - 1];
      let text;
      if (/^(-)?[\d]+$/g.test(page)) {
        const name = pages[pages.length - 2];
        const formattedName = `${name}-id`;
        text = intl.formatMessage({ id: `breadcrumbs.${formattedName}` });
        text += '#';
        text += intl.formatMessage({ id: 'breadcrumbs.id' }, { id: page });
      } else {
        text = intl.formatMessage({ id: `breadcrumbs.${page}` });
      }
      return (
        <BreadcrumbItem as={Link} to={elem.pathname + elem.search} key={uuid()} state={elem.state}>
          {text}
        </BreadcrumbItem>
      );
    });

  if (locationPath.length <= 1) return <div className={styles.breadcrumbPlaceholder} />;

  return <BreadcrumbNavigation>{formatCrumbs()}</BreadcrumbNavigation>;
};

export default Breadcrumbs;
